import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './authContext';
import { useColors } from './ColorContext';

const ClubContext = createContext();

export const useClub = () => useContext(ClubContext);

export const ClubProvider = ({ children }) => {
    const { user } = useAuth();
    const { updateColors } = useColors();
    const [currentDate, setCurrentDate] = useState(new Date());
    const [clubData, setClubData] = useState(null);
    const [loading, setLoading] = useState(false);

    // Historical viewing state
    const [viewingHistoricClub, setViewingHistoricClub] = useState(null); // null = current club, object = historic club data
    const isViewingHistoric = viewingHistoricClub !== null;

    // Helper function to define endpoint based on sport if needed in future, 
    // currently defaults to football as per request context.
    // Ideally this should come from user settings or prop.
    const getEndpoint = () => 'ficha-clube-football';

    const fetchClubData = useCallback(async (dateToFetch) => {
        if (!user || !user.email) return;

        // Avoid double fetching if user is not ready
        setLoading(true);
        try {
            const endpoint = getEndpoint(); // Extendable for other sports
            const dateStr = dateToFetch.toISOString();

            const response = await axios.get(`/api/${endpoint}/${user.email}?date=${dateStr}`);

            if (response.data) {
                setClubData(response.data);

                // Update global colors based on the fetched club data
                if (response.data.corPrincipal && response.data.corSecundaria) {
                    updateColors({
                        primary: response.data.corPrincipal,
                        secondary: response.data.corSecundaria
                    });
                }
            }
        } catch (error) {
            console.error("Error fetching club data:", error);
        } finally {
            setLoading(false);
        }
    }, [user, updateColors]);

    useEffect(() => {
        if (user) {
            fetchClubData(currentDate);
        }
    }, [fetchClubData, currentDate, user]);

    const changeDate = useCallback((newDate) => {
        setCurrentDate(newDate);
    }, []);

    const refreshClubData = useCallback(() => {
        fetchClubData(currentDate);
    }, [fetchClubData, currentDate]);

    // Fetch the current/active club without date filtering (for use after save operations)
    const refreshCurrentClub = useCallback(async () => {
        if (!user || !user.email) return;

        setLoading(true);
        try {
            const endpoint = getEndpoint();
            // No date parameter = fetch current active club
            const response = await axios.get(`/api/${endpoint}/${user.email}`);

            if (response.data) {
                setClubData(response.data);
                // Also update currentDate to now to stay in sync
                setCurrentDate(new Date());

                if (response.data.corPrincipal && response.data.corSecundaria) {
                    updateColors({
                        primary: response.data.corPrincipal,
                        secondary: response.data.corSecundaria
                    });
                }
            }
        } catch (error) {
            console.error("Error fetching current club data:", error);
        } finally {
            setLoading(false);
        }
    }, [user, updateColors]);

    // Enter historical viewing mode
    const viewHistoricClub = useCallback((historicClubData) => {
        setViewingHistoricClub(historicClubData);
        // Optionally update colors to historic club colors
        if (historicClubData.corPrincipal && historicClubData.corSecundaria) {
            updateColors({
                primary: historicClubData.corPrincipal,
                secondary: historicClubData.corSecundaria
            });
        }
    }, [updateColors]);

    // Exit historical viewing mode - return to current club
    const exitHistoricView = useCallback(() => {
        setViewingHistoricClub(null);
        // Restore current club colors
        if (clubData && clubData.corPrincipal && clubData.corSecundaria) {
            updateColors({
                primary: clubData.corPrincipal,
                secondary: clubData.corSecundaria
            });
        }
    }, [clubData, updateColors]);

    const value = React.useMemo(() => ({
        clubData,
        currentDate,
        changeDate,
        refreshClubData,
        refreshCurrentClub,
        loading,
        // Historical viewing
        viewingHistoricClub,
        isViewingHistoric,
        viewHistoricClub,
        exitHistoricView
    }), [
        clubData,
        currentDate,
        changeDate,
        refreshClubData,
        refreshCurrentClub,
        loading,
        viewingHistoricClub,
        isViewingHistoric,
        viewHistoricClub,
        exitHistoricView
    ]);

    return (
        <ClubContext.Provider value={value}>
            {children}
        </ClubContext.Provider>
    );
};

